import { useState, ReactNode } from 'react';
import { Rss, Upload, Download, Music2, Image as ImageIcon, Sparkles, ChevronDown, ChevronUp, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useUploadFile } from '@/hooks/useUploadFile';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useToast } from '@/hooks/useToast';
import { MUSIC_TRACK_KIND, generateRandomGradient } from '@/lib/musicTypes';
import { Badge } from '@/components/ui/badge';
import { GenreSelector } from '@/components/GenreSelector';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface ParsedTrack {
  title: string;
  audioUrl: string;
  imageUrl?: string;
  artist?: string;
  album?: string;
  trackNumber?: string;
  duration?: number;
  description?: string;
  releaseDate?: string;
  genres: string[];
  gradient?: string[];
  isAiGenerated: boolean;
  expanded: boolean;
}

interface AlbumInfo {
  title: string;
  artist: string;
  imageUrl?: string;
  releaseDate?: string;
  gradient?: string[];
}

interface ImportFromRSSDialogProps {
  children?: ReactNode;
}

export function ImportFromRSSDialog({ children }: ImportFromRSSDialogProps = {}) {
  const [open, setOpen] = useState(false);
  const [rssUrl, setRssUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isParsed, setIsParsed] = useState(false);
  const [tracks, setTracks] = useState<ParsedTrack[]>([]);
  const [albumInfo, setAlbumInfo] = useState<AlbumInfo | null>(null);
  const [hasAlbum, setHasAlbum] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishedCount, setPublishedCount] = useState(0);

  const { user } = useCurrentUser();
  const { mutateAsync: createEvent } = useNostrPublish();
  const { mutateAsync: uploadFile } = useUploadFile();
  const { toast } = useToast();

  const handleFetchRSS = async () => {
    if (!rssUrl.trim()) {
      toast({
        title: 'Missing URL',
        description: 'Please enter an RSS feed URL.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      // Fetch RSS feed
      const response = await fetch(rssUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch RSS feed');
      }

      const xmlText = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

      // Check for parsing errors
      const parseError = xmlDoc.querySelector('parsererror');
      if (parseError) {
        throw new Error('Invalid RSS/XML format');
      }

      // Try to parse as podcast RSS (most common music RSS format)
      const channel = xmlDoc.querySelector('channel');
      if (!channel) {
        throw new Error('Invalid RSS feed: No channel element found');
      }

      // Extract album info from channel
      const channelTitle = channel.querySelector('title')?.textContent || '';
      const channelAuthor =
        channel.querySelector('itunes\\:author')?.textContent ||
        channel.querySelector('author')?.textContent ||
        '';
      const channelImage =
        channel.querySelector('itunes\\:image')?.getAttribute('href') ||
        channel.querySelector('image > url')?.textContent ||
        '';
      const channelReleaseDate = channel.querySelector('pubDate')?.textContent || '';

      // Check if this looks like an album (common channel info)
      const items = Array.from(xmlDoc.querySelectorAll('item'));
      const hasCommonAlbum = items.length > 1 &&
        items.every((item) => {
          const itemAlbum = item.querySelector('itunes\\:album')?.textContent;
          return itemAlbum && itemAlbum === items[0].querySelector('itunes\\:album')?.textContent;
        });

      if (hasCommonAlbum || channelTitle) {
        const firstItemAlbum = items[0]?.querySelector('itunes\\:album')?.textContent;
        setAlbumInfo({
          title: firstItemAlbum || channelTitle,
          artist: channelAuthor,
          imageUrl: channelImage,
          releaseDate: channelReleaseDate ? new Date(channelReleaseDate).toISOString().split('T')[0] : undefined,
          gradient: channelImage ? undefined : generateRandomGradient(),
        });
        setHasAlbum(true);
      }

      // Parse tracks
      const parsedTracks: ParsedTrack[] = [];

      for (const item of items) {
        const title = item.querySelector('title')?.textContent || 'Untitled Track';

        // Get audio URL from enclosure
        const enclosure = item.querySelector('enclosure[type^="audio"]');
        const audioUrl = enclosure?.getAttribute('url') || '';

        if (!audioUrl) continue; // Skip if no audio URL

        // Extract metadata
        const artist =
          item.querySelector('itunes\\:author')?.textContent ||
          item.querySelector('author')?.textContent ||
          channelAuthor ||
          '';

        const album =
          item.querySelector('itunes\\:album')?.textContent ||
          (hasCommonAlbum ? channelTitle : '');

        const imageUrl =
          item.querySelector('itunes\\:image')?.getAttribute('href') ||
          channelImage ||
          '';

        const description =
          item.querySelector('description')?.textContent ||
          item.querySelector('itunes\\:summary')?.textContent ||
          '';

        const durationStr = item.querySelector('itunes\\:duration')?.textContent;
        let duration: number | undefined;
        if (durationStr) {
          // Duration can be in HH:MM:SS or seconds
          if (durationStr.includes(':')) {
            const parts = durationStr.split(':').map(Number);
            if (parts.length === 3) {
              duration = parts[0] * 3600 + parts[1] * 60 + parts[2];
            } else if (parts.length === 2) {
              duration = parts[0] * 60 + parts[1];
            }
          } else {
            duration = parseInt(durationStr);
          }
        }

        // Get track number from itunes:order or parse from title
        const trackNumber = item.querySelector('itunes\\:order')?.textContent || '';

        // Get genres/categories
        const categories = Array.from(item.querySelectorAll('category, itunes\\:category'))
          .map((cat) => cat.getAttribute('text') || cat.textContent || '')
          .filter(Boolean);

        // Get release date
        const pubDate = item.querySelector('pubDate')?.textContent;
        const releaseDate = pubDate ? new Date(pubDate).toISOString().split('T')[0] : '';

        parsedTracks.push({
          title,
          audioUrl,
          imageUrl: imageUrl || undefined,
          artist,
          album,
          trackNumber,
          duration,
          description,
          releaseDate,
          genres: categories,
          gradient: imageUrl ? undefined : generateRandomGradient(),
          isAiGenerated: false,
          expanded: false,
        });
      }

      if (parsedTracks.length === 0) {
        throw new Error('No audio tracks found in RSS feed');
      }

      setTracks(parsedTracks);
      setIsParsed(true);

      toast({
        title: 'RSS feed parsed!',
        description: `Found ${parsedTracks.length} track${parsedTracks.length !== 1 ? 's' : ''}.`,
      });
    } catch (error) {
      console.error('Error parsing RSS:', error);
      toast({
        title: 'Failed to parse RSS',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTrackChange = (index: number, field: keyof ParsedTrack, value: string | string[] | boolean) => {
    const newTracks = [...tracks];
    // @ts-expect-error - field is dynamic
    newTracks[index][field] = value;
    setTracks(newTracks);
  };

  const toggleTrackExpanded = (index: number) => {
    const newTracks = [...tracks];
    newTracks[index].expanded = !newTracks[index].expanded;
    setTracks(newTracks);
  };

  const handleRandomizeGradient = (index: number) => {
    const newTracks = [...tracks];
    newTracks[index].gradient = generateRandomGradient();
    newTracks[index].imageUrl = undefined;
    setTracks(newTracks);
  };

  const handleAlbumRandomizeGradient = () => {
    if (albumInfo) {
      setAlbumInfo({
        ...albumInfo,
        gradient: generateRandomGradient(),
        imageUrl: undefined,
      });
    }
  };

  const handlePublishToNostr = async () => {
    if (tracks.length === 0) return;

    setIsPublishing(true);
    setPublishedCount(0);

    try {
      // Publish each track
      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i];

        // Generate unique identifier
        const trackId = `${track.title.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}-${i}`;

        // Build tags
        const tags: string[][] = [
          ['d', trackId],
          ['title', track.title.trim()],
          ['url', track.audioUrl],
          ['t', 'music'],
          ['alt', `Music track: ${track.title.trim()} by ${track.artist || user?.profile?.name || 'Artist'}`],
        ];

        // Add genres
        if (track.genres.length > 0) {
          track.genres.forEach((genre) => {
            tags.push(['t', genre.toLowerCase()]);
          });
        }

        // Add image
        if (track.imageUrl) {
          tags.push(['image', track.imageUrl]);
        } else if (hasAlbum && albumInfo?.imageUrl) {
          // Use album artwork if track doesn't have its own
          tags.push(['image', albumInfo.imageUrl]);
        }

        // Add optional metadata
        const artistName = track.artist || (hasAlbum ? albumInfo?.artist : '') || '';
        if (artistName) {
          tags.push(['artist', artistName]);
        }

        const albumName = track.album || (hasAlbum ? albumInfo?.title : '') || '';
        if (albumName) {
          tags.push(['album', albumName]);
        }

        if (track.trackNumber) {
          tags.push(['track_number', track.trackNumber]);
        }

        const releaseDate = track.releaseDate || (hasAlbum ? albumInfo?.releaseDate : '') || '';
        if (releaseDate) {
          tags.push(['released', releaseDate]);
        }

        if (track.duration) {
          tags.push(['duration', track.duration.toString()]);
        }

        if (track.isAiGenerated) {
          tags.push(['ai-generated', 'true']);
        }

        // Content field for description
        const content = track.description?.trim() || '';

        // Publish the event
        await createEvent({
          kind: MUSIC_TRACK_KIND,
          content,
          tags,
        });

        setPublishedCount(i + 1);
      }

      toast({
        title: 'All tracks published!',
        description: `Successfully published ${tracks.length} track${tracks.length !== 1 ? 's' : ''} to Nostr.`,
      });

      // Reset form
      setOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error publishing tracks:', error);
      toast({
        title: 'Publishing failed',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const resetForm = () => {
    setRssUrl('');
    setIsParsed(false);
    setTracks([]);
    setAlbumInfo(null);
    setHasAlbum(false);
    setPublishedCount(0);
  };

  if (!user) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button className="gap-2">
            <Rss className="h-4 w-4" />
            Import from RSS
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Import from RSS Feed</DialogTitle>
          <DialogDescription>
            Paste your music RSS feed URL to import tracks and publish them to Nostr
          </DialogDescription>
        </DialogHeader>

        {!isParsed ? (
          <div className="space-y-4">
            {/* RSS URL Input */}
            <div className="space-y-2">
              <Label htmlFor="rss-url">RSS Feed URL</Label>
              <div className="flex gap-2">
                <Input
                  id="rss-url"
                  type="url"
                  value={rssUrl}
                  onChange={(e) => setRssUrl(e.target.value)}
                  placeholder="https://example.com/podcast.rss"
                  disabled={isLoading}
                  className="h-9"
                />
                <Button
                  onClick={handleFetchRSS}
                  disabled={isLoading || !rssUrl.trim()}
                  variant="ghost"
                  className="gap-2 bg-white/[0.07] hover:bg-white/[0.12] h-9"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      Fetch
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Supports podcast RSS feeds with audio enclosures (iTunes/podcast format)
              </p>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col space-y-4 overflow-hidden">
            {/* Album Info Section */}
            {hasAlbum && albumInfo && (
              <div className="space-y-3 p-4 bg-muted/30 rounded-lg border border-white/[0.11]">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Album Information</Label>
                  <Switch
                    checked={hasAlbum}
                    onCheckedChange={setHasAlbum}
                  />
                </div>

                {hasAlbum && (
                  <div className="grid grid-cols-[auto_1fr] gap-4">
                    {/* Album artwork preview */}
                    <div className="flex-shrink-0">
                      {albumInfo.imageUrl ? (
                        <div
                          className="w-32 h-32 rounded-lg bg-cover bg-center"
                          style={{ backgroundImage: `url(${albumInfo.imageUrl})` }}
                        />
                      ) : albumInfo.gradient && albumInfo.gradient.length > 0 ? (
                        <div
                          className="w-32 h-32 rounded-lg flex items-center justify-center"
                          style={{ background: `linear-gradient(135deg, ${albumInfo.gradient.join(', ')})` }}
                        >
                          <Music2 className="h-10 w-10 text-white/80" />
                        </div>
                      ) : (
                        <div className="w-32 h-32 rounded-lg border border-dashed border-white/[0.11] bg-white/[0.11] flex items-center justify-center">
                          <ImageIcon className="h-10 w-10 text-muted-foreground" />
                        </div>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-2 w-full gap-1"
                        onClick={handleAlbumRandomizeGradient}
                      >
                        <Sparkles className="h-3 w-3" />
                        Random
                      </Button>
                    </div>

                    {/* Album fields */}
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor="album-title" className="text-xs">Album Title</Label>
                          <Input
                            id="album-title"
                            value={albumInfo.title}
                            onChange={(e) => setAlbumInfo({ ...albumInfo, title: e.target.value })}
                            placeholder="Album name"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="album-artist" className="text-xs">Artist</Label>
                          <Input
                            id="album-artist"
                            value={albumInfo.artist}
                            onChange={(e) => setAlbumInfo({ ...albumInfo, artist: e.target.value })}
                            placeholder="Artist name"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor="album-image" className="text-xs">Image URL</Label>
                          <Input
                            id="album-image"
                            value={albumInfo.imageUrl || ''}
                            onChange={(e) => setAlbumInfo({ ...albumInfo, imageUrl: e.target.value, gradient: undefined })}
                            placeholder="https://..."
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="album-date" className="text-xs">Release Date</Label>
                          <Input
                            id="album-date"
                            type="date"
                            value={albumInfo.releaseDate || ''}
                            onChange={(e) => setAlbumInfo({ ...albumInfo, releaseDate: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tracks List */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold">
                  Tracks ({tracks.length})
                </Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const allExpanded = tracks.every((t) => t.expanded);
                    setTracks(tracks.map((t) => ({ ...t, expanded: !allExpanded })));
                  }}
                >
                  {tracks.every((t) => t.expanded) ? 'Collapse All' : 'Expand All'}
                </Button>
              </div>

              <ScrollArea className="flex-1 border rounded-lg border-white/[0.11]">
                <div className="p-4 space-y-2">
                  {tracks.map((track, index) => (
                    <div key={index} className="border rounded-lg border-white/[0.11] bg-muted/20">
                      {/* Track Header */}
                      <div
                        className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/30"
                        onClick={() => toggleTrackExpanded(index)}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="flex-shrink-0 w-8 h-8 rounded bg-primary/10 flex items-center justify-center text-sm font-medium">
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{track.title}</div>
                            <div className="text-xs text-muted-foreground truncate">
                              {track.artist || 'Unknown Artist'} {track.album && `• ${track.album}`}
                            </div>
                          </div>
                          {track.genres.length > 0 && (
                            <div className="hidden sm:flex gap-1">
                              {track.genres.slice(0, 2).map((genre) => (
                                <Badge key={genre} variant="secondary" className="text-xs">
                                  {genre}
                                </Badge>
                              ))}
                              {track.genres.length > 2 && (
                                <Badge variant="secondary" className="text-xs">
                                  +{track.genres.length - 2}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                        {track.expanded ? (
                          <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>

                      {/* Track Details (Expanded) */}
                      <Collapsible open={track.expanded}>
                        <CollapsibleContent>
                          <Separator />
                          <div className="p-4 space-y-4">
                            {/* Preview and Basic Info */}
                            <div className="grid grid-cols-[auto_1fr] gap-4">
                              {/* Artwork preview */}
                              <div className="flex-shrink-0">
                                {track.imageUrl ? (
                                  <div
                                    className="w-24 h-24 rounded-lg bg-cover bg-center"
                                    style={{ backgroundImage: `url(${track.imageUrl})` }}
                                  />
                                ) : track.gradient && track.gradient.length > 0 ? (
                                  <div
                                    className="w-24 h-24 rounded-lg flex items-center justify-center"
                                    style={{ background: `linear-gradient(135deg, ${track.gradient.join(', ')})` }}
                                  >
                                    <Music2 className="h-8 w-8 text-white/80" />
                                  </div>
                                ) : (
                                  <div className="w-24 h-24 rounded-lg border border-dashed border-white/[0.11] bg-white/[0.11] flex items-center justify-center">
                                    <ImageIcon className="h-8 w-8 text-muted-foreground" />
                                  </div>
                                )}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="mt-2 w-full gap-1"
                                  onClick={() => handleRandomizeGradient(index)}
                                >
                                  <Sparkles className="h-3 w-3" />
                                  Random
                                </Button>
                              </div>

                              {/* Track fields */}
                              <div className="space-y-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">Title</Label>
                                  <Input
                                    value={track.title}
                                    onChange={(e) => handleTrackChange(index, 'title', e.target.value)}
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="space-y-1">
                                    <Label className="text-xs">Artist</Label>
                                    <Input
                                      value={track.artist || ''}
                                      onChange={(e) => handleTrackChange(index, 'artist', e.target.value)}
                                      placeholder={hasAlbum ? albumInfo?.artist : ''}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">Album</Label>
                                    <Input
                                      value={track.album || ''}
                                      onChange={(e) => handleTrackChange(index, 'album', e.target.value)}
                                      placeholder={hasAlbum ? albumInfo?.title : ''}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Additional fields */}
                            <div className="grid grid-cols-3 gap-2">
                              <div className="space-y-1">
                                <Label className="text-xs">Track #</Label>
                                <Input
                                  value={track.trackNumber || ''}
                                  onChange={(e) => handleTrackChange(index, 'trackNumber', e.target.value)}
                                  placeholder="1"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Duration</Label>
                                <Input
                                  value={track.duration ? `${Math.floor(track.duration / 60)}:${(track.duration % 60).toString().padStart(2, '0')}` : ''}
                                  readOnly
                                  disabled
                                  className="bg-muted/50"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Release Date</Label>
                                <Input
                                  type="date"
                                  value={track.releaseDate || ''}
                                  onChange={(e) => handleTrackChange(index, 'releaseDate', e.target.value)}
                                  placeholder={hasAlbum ? albumInfo?.releaseDate : ''}
                                />
                              </div>
                            </div>

                            {/* Image URL */}
                            <div className="space-y-1">
                              <Label className="text-xs">Image URL</Label>
                              <Input
                                value={track.imageUrl || ''}
                                onChange={(e) => {
                                  const newTracks = [...tracks];
                                  newTracks[index].imageUrl = e.target.value;
                                  newTracks[index].gradient = undefined;
                                  setTracks(newTracks);
                                }}
                                placeholder={hasAlbum ? albumInfo?.imageUrl || 'Will use album artwork' : 'https://...'}
                              />
                            </div>

                            {/* Genres */}
                            <div className="space-y-1">
                              <Label className="text-xs">Genres</Label>
                              <GenreSelector
                                selectedGenres={track.genres}
                                onChange={(genres) => handleTrackChange(index, 'genres', genres)}
                              />
                            </div>

                            {/* Description */}
                            {track.description && (
                              <div className="space-y-1">
                                <Label className="text-xs">Description</Label>
                                <Textarea
                                  value={track.description}
                                  onChange={(e) => handleTrackChange(index, 'description', e.target.value)}
                                  rows={3}
                                  className="text-sm"
                                />
                              </div>
                            )}

                            {/* AI-Generated */}
                            <div className="flex items-center space-x-2">
                              <Switch
                                checked={track.isAiGenerated}
                                onCheckedChange={(checked) => handleTrackChange(index, 'isAiGenerated', checked)}
                              />
                              <Label className="text-sm">AI-Generated</Label>
                            </div>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsParsed(false);
                  setTracks([]);
                  setAlbumInfo(null);
                  setHasAlbum(false);
                }}
                disabled={isPublishing}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={handlePublishToNostr}
                disabled={isPublishing || tracks.length === 0}
                className="flex-1 gap-2"
              >
                {isPublishing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Publishing {publishedCount}/{tracks.length}...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Publish to Nostr
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
